import React, { useState } from 'react';
import { Card, Tabs, Table, Button, Space, Tag, Modal, Form, Input, Select, Switch, message, Divider, Tree, Row, Col, DatePicker } from 'antd';
import { UserOutlined, TeamOutlined, SettingOutlined, LockOutlined, KeyOutlined, PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, HistoryOutlined, SearchOutlined } from '@ant-design/icons';

const { TabPane } = Tabs;
const { Option } = Select;
const { RangePicker } = DatePicker;

const SystemManagement = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(false);
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [roleModalVisible, setRoleModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [userForm] = Form.useForm();
  const [roleForm] = Form.useForm();

  // 模拟用户数据
  const usersData = [
    {
      id: 1,
      username: 'admin',
      name: '管理员',
      role: 'admin',
      department: '系统管理部',
      phone: '13800138000',
      email: 'admin@example.com',
      status: 'active',
      lastLogin: '2025-03-18 15:30:22',
    },
    {
      id: 2,
      username: 'security',
      name: '安全主管',
      role: 'security',
      department: '安全部',
      phone: '13900139000',
      email: 'security@example.com',
      status: 'active',
      lastLogin: '2025-03-18 14:25:18',
    },
    {
      id: 3,
      username: 'operator',
      name: '运维人员',
      role: 'operator',
      department: '运维部',
      phone: '13700137000',
      email: 'operator@example.com',
      status: 'inactive',
      lastLogin: '2025-03-17 10:15:30',
    },
  ];

  // 模拟角色数据
  const rolesData = [
    {
      id: 1,
      name: 'admin',
      description: '系统管理员',
      permissions: ['all'],
      userCount: 1,
    },
    {
      id: 2,
      name: 'security',
      description: '安全主管',
      permissions: ['video_view', 'video_edit', 'warning_view', 'warning_process', 'people_flow_view', 'push_view', 'push_send'],
      userCount: 1,
    },
    {
      id: 3,
      name: 'operator',
      description: '运维人员',
      permissions: ['video_view', 'warning_view', 'people_flow_view'],
      userCount: 1,
    },
  ];

  // 模拟日志数据
  const logsData = [
    {
      id: 1,
      username: 'admin',
      action: '登录系统',
      ip: '192.168.1.100',
      time: '2025-03-18 15:30:22',
      status: 'success',
    },
    {
      id: 2,
      username: 'security',
      action: '处理警告',
      ip: '192.168.1.101',
      time: '2025-03-18 14:25:18',
      status: 'success',
    },
    {
      id: 3,
      username: 'operator',
      action: '查看视频',
      ip: '192.168.1.102',
      time: '2025-03-18 13:15:45',
      status: 'success',
    },
    {
      id: 4,
      username: 'unknown',
      action: '登录尝试',
      ip: '192.168.1.103',
      time: '2025-03-18 12:10:33',
      status: 'failed',
    },
  ];

  // 模拟系统配置数据
  const systemConfig = {
    siteName: 'AI视频监控平台',
    siteUrl: 'https://example.com',
    adminEmail: 'admin@example.com',
    maxUploadSize: 50,
    allowedFileTypes: 'jpg,png,mp4,avi',
    sessionTimeout: 30,
    enableNotification: true,
    enableSms: true,
    enableEmail: true,
    enableVoice: false,
    backupEnabled: true,
    backupTime: '03:00',
    backupRetention: 7,
  };

  // 用户表格列定义
  const userColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role) => {
        let color = 'blue';
        if (role === 'admin') {
          color = 'red';
        } else if (role === 'security') {
          color = 'green';
        }
        return <Tag color={color}>{role}</Tag>;
      },
    },
    {
      title: '部门',
      dataIndex: 'department',
      key: 'department',
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        return status === 'active' ? 
          <Tag color="green">活跃</Tag> : 
          <Tag color="red">禁用</Tag>;
      },
    },
    {
      title: '最后登录',
      dataIndex: 'lastLogin',
      key: 'lastLogin',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="link" 
            size="small"
            onClick={() => handleEditUser(record)}
          >
            编辑
          </Button>
          <Button 
            type="link" 
            size="small"
            onClick={() => handleResetPassword(record)}
          >
            重置密码
          </Button>
          <Button 
            type="link" 
            danger 
            size="small"
            onClick={() => handleDeleteUser(record)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // 角色表格列定义
  const roleColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '角色名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: '权限',
      dataIndex: 'permissions',
      key: 'permissions',
      render: (permissions) => {
        if (permissions.includes('all')) {
          return <Tag color="red">所有权限</Tag>;
        }
        return (
          <span>
            {permissions.slice(0, 2).map(perm => (
              <Tag color="blue" key={perm}>{perm}</Tag>
            ))}
            {permissions.length > 2 && <Tag color="blue">+{permissions.length - 2}</Tag>}
          </span>
        );
      },
    },
    {
      title: '用户数',
      dataIndex: 'userCount',
      key: 'userCount',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="link" 
            size="small"
            onClick={() => handleEditRole(record)}
          >
            编辑
          </Button>
          <Button 
            type="link" 
            danger 
            size="small"
            onClick={() => handleDeleteRole(record)}
            disabled={record.name === 'admin'}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // 日志表格列定义
  const logColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '用户',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '操作',
      dataIndex: 'action',
      key: 'action',
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
    },
    {
      title: '时间',
      dataIndex: 'time',
      key: 'time',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        return status === 'success' ? 
          <Tag color="green">成功</Tag> : 
          <Tag color="red">失败</Tag>;
      },
    },
  ];

  // 处理编辑用户
  const handleEditUser = (record) => {
    setSelectedRecord(record);
    userForm.setFieldsValue(record);
    setUserModalVisible(true);
  };

  // 处理重置密码
  const handleResetPassword = (record) => {
    Modal.confirm({
      title: '重置密码',
      content: `确定要重置用户 "${record.username}" 的密码吗？`,
      onOk() {
        message.success(`用户 ${record.username} 的密码已重置为 123456`);  
      },
    });
  };

  // 处理删除用户
  const handleDeleteUser = (record) => {
    if (record.username === 'admin') {
      message.error('不能删除管理员账号');
      return;
    }
    
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除用户 "${record.username}" 吗？`,
      onOk() {
        message.success(`用户 ${record.username} 已删除`);  
      },
    });
  };

  // 处理编辑角色
  const handleEditRole = (record) => {
    setSelectedRecord(record);
    roleForm.setFieldsValue(record);
    setRoleModalVisible(true);
  };

  // 处理删除角色
  const handleDeleteRole = (record) => {
    if (record.name === 'admin') {
      message.error('不能删除管理员角色');
      return;
    }
    
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除角色 "${record.name}" 吗？`,
      onOk() {
        message.success(`角色 ${record.name} 已删除`);  
      },
    });
  };

  // 处理用户表单提交
  const handleUserFormSubmit = () => {
    userForm.validateFields().then(values => {
      console.log('用户表单提交:', values);
      message.success(`用户${selectedRecord ? '更新' : '添加'}成功`);
      setUserModalVisible(false);
    });
  };

  // 处理角色表单提交
  const handleRoleFormSubmit = () => {
    roleForm.validateFields().then(values => {
      console.log('角色表单提交:', values);
      message.success(`角色${selectedRecord ? '更新' : '添加'}成功`);
      setRoleModalVisible(false);
    });
  };

  // 权限树数据
  const permissionTreeData = [
    {
      title: '视频管理',
      key: 'video',
      children: [
        { title: '查看视频', key: 'video_view' },
        { title: '添加视频', key: 'video_add' },
        { title: '编辑视频', key: 'video_edit' },
        { title: '删除视频', key: 'video_delete' },
      ],
    },
    {
      title: '人流检测',
      key: 'people_flow',
      children: [
        { title: '查看人流数据', key: 'people_flow_view' },
        { title: '导出人流数据', key: 'people_flow_export' },
      ],
    },
    {
      title: '警告管理',
      key: 'warning',
      children: [
        { title: '查看警告', key: 'warning_view' },
        { title: '处理警告', key: 'warning_process' },
        { title: '删除警告', key: 'warning_delete' },
      ],
    },
    {
      title: '推送管理',
      key: 'push',
      children: [
        { title: '查看推送', key: 'push_view' },
        { title: '发送推送', key: 'push_send' },
        { title: '删除推送', key: 'push_delete' },
      ],
    },
    {
      title: '系统管理',
      key: 'system',
      children: [
        { title: '用户管理', key: 'user_manage' },
        { title: '角色管理', key: 'role_manage' },
        { title: '系统配置', key: 'system_config' },
        { title: '日志查看', key: 'log_view' },
      ],
    },
  ];

  return (
    <div>
      <Card title={
        <span>
          <SettingOutlined style={{ marginRight: 8 }} />
          系统管理
        </span>
      }>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane 
            tab={
              <span>
                <UserOutlined />
                用户管理
              </span>
            } 
            key="users"
          >
            <div className="button-group">
              <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                setSelectedRecord(null);
                userForm.resetFields();
                setUserModalVisible(true);
              }}>
                添加用户
              </Button>
            </div>
            <Table 
              columns={userColumns} 
              dataSource={usersData} 
              rowKey="id" 
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </TabPane>
          
          <TabPane 
            tab={
              <span>
                <TeamOutlined />
                角色管理
              </span>
            } 
            key="roles"
          >
            <div className="button-group">
              <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                setSelectedRecord(null);
                roleForm.resetFields();
                setRoleModalVisible(true);
              }}>
                添加角色
              </Button>
            </div>
            <Table 
              columns={roleColumns} 
              dataSource={rolesData} 
              rowKey="id" 
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </TabPane>
          
          <TabPane 
            tab={
              <span>
                <SettingOutlined />
                系统配置
              </span>
            } 
            key="config"
          >
            <Form layout="vertical" initialValues={systemConfig}>
              <Divider orientation="left">基本配置</Divider>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="siteName" label="系统名称">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="siteUrl" label="系统域名">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="adminEmail" label="管理员邮箱">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              
              <Divider orientation="left">文件上传配置</Divider>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="maxUploadSize" label="最大上传大小(MB)">
                    <Input type="number" />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item name="allowedFileTypes" label="允许的文件类型">
                    <Input placeholder="例如: jpg,png,mp4,avi" />
                  </Form.Item>
                </Col>
              </Row>
              
              <Divider orientation="left">会话与通知配置</Divider>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="sessionTimeout" label="会话超时时间(分钟)">
                    <Input type="number" />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item name="enableNotification" label="启用通知" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="enableSms" label="启用短信通知" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="enableEmail" label="启用邮件通知" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="enableVoice" label="启用语音通知" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>
              
              <Divider orientation="left">备份配置</Divider>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="backupEnabled" label="启用自动备份" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="backupTime" label="备份时间">
                    <Input placeholder="例如: 03:00" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="backupRetention" label="备份保留天数">
                    <Input type="number" />
                  </Form.Item>
                </Col>
              </Row>
              
              <div style={{ marginTop: 24 }}>
                <Button type="primary">保存配置</Button>
                <Button style={{ marginLeft: 8 }}>重置</Button>
              </div>
            </Form>
          </TabPane>
          
          <TabPane 
            tab={
              <span>
                <HistoryOutlined />
                系统日志
              </span>
            } 
            key="logs"
          >
            <div className="search-bar" style={{ marginBottom: 16 }}>
              <Space>
                <Input placeholder="用户名" style={{ width: 200 }} />
                <Select placeholder="操作类型" style={{ width: 150 }} allowClear>
                  <Option value="login">登录</Option>
                  <Option value="logout">登出</Option>
                  <Option value="operation">操作</Option>
                </Select>
                <Select placeholder="状态" style={{ width: 150 }} allowClear>
                  <Option value="success">成功</Option>
                  <Option value="failed">失败</Option>
                </Select>
                <RangePicker showTime />
                <Button type="primary" icon={<SearchOutlined />}>搜索</Button>
                <Button>重置</Button>
              </Space>
            </div>
            
            <Table 
              columns={logColumns} 
              dataSource={logsData} 
              rowKey="id" 
              loading={loading}
              pagination={{
                total: logsData.length,
                pageSize: 10,
                showTotal: (total) => `共 ${total} 条`,
                showQuickJumper: true,
                showSizeChanger: true,
              }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* 用户表单模态框 */}
      <Modal
        title={selectedRecord ? '编辑用户' : '添加用户'}
        open={userModalVisible}
        onOk={handleUserFormSubmit}
        onCancel={() => setUserModalVisible(false)}
        confirmLoading={loading}
      >
        <Form form={userForm} layout="vertical">
          <Form.Item
            name="username"
            label="用户名"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input disabled={selectedRecord} />
          </Form.Item>
          {!selectedRecord && (
            <Form.Item
              name="password"
              label="密码"
              rules={[{ required: true, message: '请输入密码' }]}
            >
              <Input.Password />
            </Form.Item>
          )}
          <Form.Item
            name="name"
            label="姓名"
            rules={[{ required: true, message: '请输入姓名' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select>
              {rolesData.map(role => (
                <Option key={role.name} value={role.name}>{role.description}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="department"
            label="部门"
            rules={[{ required: true, message: '请输入部门' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="phone"
            label="手机号"
            rules={[{ required: true, message: '请输入手机号' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="status"
            label="状态"
            valuePropName="checked"
          >
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 角色表单模态框 */}
      <Modal
        title={selectedRecord ? '编辑角色' : '添加角色'}
        open={roleModalVisible}
        onOk={handleRoleFormSubmit}
        onCancel={() => setRoleModalVisible(false)}
        confirmLoading={loading}
        width={600}
      >
        <Form form={roleForm} layout="vertical">
          <Form.Item
            name="name"
            label="角色名"
            rules={[{ required: true, message: '请输入角色名' }]}
          >
            <Input disabled={selectedRecord} />
          </Form.Item>
          <Form.Item
            name="description"
            label="描述"
            rules={[{ required: true, message: '请输入角色描述' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="permissions"
            label="权限"
            rules={[{ required: true, message: '请选择权限' }]}
          >
            <Tree
              checkable
              treeData={permissionTreeData}
              defaultExpandAll
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SystemManagement;
